import { RouteObject } from "react-router-dom";
import { NotFound } from "src/routes/NotFound";
import { ProductOfferingCostCalculatorPage } from "src/routes/developments/product-offerings/ProductOfferingCostCalculatorPage";
import { ProductOfferingScopePage } from "src/routes/developments/product-offerings/ProductOfferingScopePage";
import { PlanPackagePageV2 } from "src/routes/libraries/plan-package-v2/PlanPackagePageV2";
import { AddProductOfferingConfigPage } from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage";
import { ProductOfferingConfigsPage } from "src/routes/libraries/product-offering-configs/ProductOfferingConfigsPage";
import { GlobalProductOfferings } from "src/routes/libraries/product-offerings/GlobalProductOfferings";
import { AddProductOfferingPage } from "src/routes/libraries/product-offerings/add-product-offering/AddProductOfferingPage";
import { CheckoutPricingTracePage } from "src/routes/libraries/product-offerings/checkout-pricing-trace/CheckoutPricingTracePage";
import { EditProductOfferingPage } from "src/routes/libraries/product-offerings/edit-product-offering/EditProductOfferingPage";
import { AppShell } from "src/routing/components/AppShell";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import { ScrollableParentLayout } from "src/routing/components/layouts/ScrollableParentLayout";
import {
  globalProductOfferingConfigsRoute,
  globalProductOfferingsRoute,
  planPackageRoute,
  productOfferingConfigEditRoute,
  productOfferingConfigPricingTraceRoute,
  productOfferingConfigRoute,
  productOfferingCostCalculatorRoute,
  productOfferingCreateRoute,
  productOfferingEditRoute,
  productOfferingScopeRoute,
} from "src/routing/domains/librariesPaths";
import { BeamNavbarLayout } from "../components/layouts/BeamNavbarLayout";

/**
 * `/libraries/product-offerings/*` route tree (first migrated domain).
 *
 * Role: data router domain route.
 *
 * Two layout stacks coexist during migration:
 * - **New (Beam):** `AppShell` → `BeamNavbarLayout` → page (index / list pages using `PageHeaderLayout`)
 * - **Legacy:** `AppShell` → `GlobalNavLayout` → `ScrollableParentLayout` → page (detail/wizard pages not yet on Beam nav)
 *
 * Some pages skip `ScrollableParentLayout` (e.g. scope uses its own side panel). Pages that have adopted
 * Beam's new layouts nest under the `BeamNavbarLayout` branch instead, which sits alongside `GlobalNavLayout`
 * under `AppShell`'s outlet — not under `GlobalNavLayout`, whose `PreventBrowserScroll` conflicts with the
 * document-scroll model those layouts assume.
 *
 * Path constants live in `librariesPaths.ts` (imported by `RouteUrls.ts` / pages) to avoid a cycle
 * through this file's page `Component` imports.
 * Legacy `LibrariesLayout` / `Routes.tsx` must not register these paths — see `LibrariesLayout.tsx`.
 */
const librariesProductOfferingsRoute: RouteObject = {
  path: globalProductOfferingsRoute.path,
  Component: AppShell,
  children: [
    // Pages on Beam's new document-scroll chrome (`NavbarLayout` + the page's own `PageHeaderLayout`).
    // These can't nest under `GlobalNavLayout` because its `PreventBrowserScroll` breaks document scrolling.
    {
      Component: BeamNavbarLayout,
      children: [
        { index: true, Component: GlobalProductOfferings },
        { path: productOfferingCostCalculatorRoute.path, Component: ProductOfferingCostCalculatorPage },
      ],
    },
    {
      Component: GlobalNavLayout,
      children: [
        // `ProductOfferingScopePage` uses its own internal layout to have a left side panel for filtering
        { path: productOfferingScopeRoute.path, Component: ProductOfferingScopePage },
        {
          Component: ScrollableParentLayout,
          children: [
            { path: productOfferingEditRoute.path, Component: EditProductOfferingPage },
            { path: productOfferingConfigPricingTraceRoute.path, Component: CheckoutPricingTracePage },
            { path: productOfferingCreateRoute.path, Component: AddProductOfferingPage },
          ],
        },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

/**
 * `/libraries/product-offering-configs/*` route tree.
 *
 * Role: data router domain route.
 *
 * Default stack: `AppShell` → `AuthenticatedShell` → `GlobalNavLayout` → `ScrollableParentLayout` → page.
 * Legacy `LibrariesLayout` must not register these paths — see `LibrariesLayout.tsx`.
 */
const librariesProductOfferingConfigsRoute: RouteObject = {
  path: globalProductOfferingConfigsRoute.path,
  Component: AppShell,
  children: [
    {
      Component: GlobalNavLayout,
      children: [
        {
          Component: ScrollableParentLayout,
          children: [
            { index: true, Component: ProductOfferingConfigsPage },
            { path: productOfferingConfigEditRoute.path, Component: AddProductOfferingConfigPage },
            { path: productOfferingConfigRoute.path, Component: AddProductOfferingConfigPage },
          ],
        },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

/**
 * `/libraries/plan-packages/*` route tree.
 *
 * Role: data router domain route.
 *
 * Stack: `AppShell` → `BeamNavbarLayout` → `PlanPackagePageV2`.
 *
 * The page mounts on both the bare details URL (shown as the Overview tab) and a `*` splat so Beam's route
 * tabs can match the tab segments. When the `PLAN_PACKAGES_V2` flag is off, it redirects to the legacy
 * `/libraries/plan/*` details page.
 *
 * TODO: update this description once the feature flag and legacy pages are removed.
 */
const librariesPlanPackagesRoute: RouteObject = {
  path: planPackageRoute.path,
  Component: AppShell,
  children: [
    {
      Component: BeamNavbarLayout,
      children: [
        { index: true, Component: PlanPackagePageV2 },
        { path: "*", Component: PlanPackagePageV2 },
      ],
    },
  ],
};

/**
 * Migrated `/libraries/*` route trees registered in `Router.tsx`.
 *
 * Kept as sibling top-level prefixes for now so unmigrated `/libraries/*` URLs still fall through
 * to legacy `LibrariesLayout`. Consolidate under a single `path: "/libraries"` parent once the
 * full libraries domain has migrated off the legacy catch-all.
 */
export const librariesRoutes = [
  librariesProductOfferingsRoute,
  librariesProductOfferingConfigsRoute,
  librariesPlanPackagesRoute,
];
